/**
 * A speaker, resolved (D192) — the pack's optional presentation facts filled
 * from the defaults, so every reader sees one complete shape.
 *
 * @remarks
 * Lives in the engine for the same reason the map's presentation does
 * (`map/map-presentation.ts`): more than one seam reads it. The host puts it
 * on the session view for the shell; the marketing terminal hands it to the
 * 404 page, which draws lines with no React and no host. One resolution,
 * every caller.
 *
 * The one default is the metronome (Mike's D99 ruling): stepped, evenly, at
 * 14 ms — zero variance, arriving complete, the way she thinks. It was the
 * engine's own cadence for its one named narrator; it is now what any
 * speaker gets for saying nothing about how it arrives. The color has no
 * default here: absent, the shell paints its own voice tint, and a line
 * carries a color only when the pack chose one.
 *
 * **The line label (D232; Calder's `actors-content.md` §6, Mike's ruling 5).**
 * A speaker that is some ACTOR's voice is EMBODIED: a body across the room,
 * and the name is the transcript's substitute for looking at them. Its
 * lines carry the speaker's name as REAL TEXT in the entry — never a
 * pseudo-element, never CSS `content`, for D85's reason: the export and the
 * paste are the media this exists for. A disembodied voice (EURYDICE, a
 * voice in the player's ear) keeps its bare quotation marks, because a
 * voice in your ear does not announce itself — and the asymmetry is what
 * makes a two-voice transcript legible on one page: quoted-and-unprefixed
 * is her, quoted-and-named is somebody in the room. The label is the
 * speaker's `name` as declared, exactly as the export has always printed a
 * labeled line (`The Tide: the water comes back`).
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition, SpeakerDefinition, SpeakerId, SpeakerReveal } from '@orpheus/content-schema';

/** One speaker as the shell needs it: the reveal always complete, the color only when chosen, the label only when embodied. */
export interface ResolvedSpeaker {
	readonly color?: string;
	/**
	 * D232: the name an EMBODIED speaker's lines carry as real text before
	 * the words — present only for a speaker some actor speaks as. A
	 * disembodied voice has none, and its lines print as authored.
	 */
	readonly lineLabel?: string;
	readonly name: string;
	readonly reveal: SpeakerReveal;
	readonly speakerId: SpeakerId;
}

/** What a speaker's lines do when the pack says nothing (D99): the metronome. */
export const DEFAULT_SPEAKER_REVEAL: SpeakerReveal = { cadence: 'stepped', stepMs: 14 };

/**
 * Resolve one declared speaker.
 *
 * @param speaker - The declaration
 * @returns The speaker, complete
 */
export const resolveSpeaker = (speaker: SpeakerDefinition): ResolvedSpeaker => ({
	...(speaker.color === undefined ? {} : { color: speaker.color }),
	name: speaker.name,
	reveal: speaker.reveal ?? DEFAULT_SPEAKER_REVEAL,
	speakerId: speaker.speakerId,
});

/**
 * The ids of the speakers some actor speaks as (D232) — the embodied voices.
 *
 * @param contentPack - The parsed pack
 * @returns The speaker ids, each once
 */
export const embodiedSpeakerIdsOf = (contentPack: ContentPackDefinition): ReadonlySet<string> =>
	new Set(contentPack.actors.flatMap((actor) => (actor.speakerId === undefined ? [] : [actor.speakerId])));

/**
 * Resolve every declared speaker of a pack, in declared order, each with its
 * line label when an actor speaks as it (D232).
 *
 * @param contentPack - The parsed pack
 * @returns The speakers, complete
 */
export const resolveSpeakers = (contentPack: ContentPackDefinition): readonly ResolvedSpeaker[] => {
	const embodied = embodiedSpeakerIdsOf(contentPack);

	return contentPack.speakers.map((speaker) => ({
		...resolveSpeaker(speaker),
		...(embodied.has(speaker.speakerId) ? { lineLabel: speaker.name } : {}),
	}));
};

/**
 * The speakers whose lines the transcript EXPORT labels by name, with the
 * label (D86's medium with no CSS).
 *
 * @remarks
 * Two rules, one map. Mike's ruling of 2026-09-17 (Calder's `who-said-that`
 * note) for the disembodied voices: a label when the speaker is UNQUOTED
 * (an unquoted voice without a label prints indistinguishably from the
 * world), or when there is more than one such voice to tell apart (two
 * quoted voices print indistinguishably from each other). D232 for the
 * embodied ones: always — the label is the body's signature in every
 * medium, and an actor's voice never counts toward the disembodied tally,
 * because a named line is already told apart from an unnamed one. So a pack
 * whose second voice is a person in a room keeps its narrator bare, exactly
 * as Calder's §6 rules: "EURYDICE's does not, and never will."
 *
 * @param contentPack - The parsed pack
 * @returns The labels, by speaker id
 */
export const exportLabelsBySpeakerOf = (contentPack: ContentPackDefinition): ReadonlyMap<string, string> => {
	const embodied = embodiedSpeakerIdsOf(contentPack);
	const disembodied = contentPack.speakers.filter((speaker) => !embodied.has(speaker.speakerId));
	const isEveryDisembodiedLabeled = disembodied.length > 1;

	return new Map(
		contentPack.speakers
			.filter((speaker) => embodied.has(speaker.speakerId) || isEveryDisembodiedLabeled || !speaker.isQuoted)
			.map((speaker) => [speaker.speakerId, speaker.name] as const),
	);
};
